import React, { useState, useEffect } from 'react';
import { getAllCourses, deleteCourse, getUserProfile } from '../../../firebase/unifiedDataService';
import { useUnifiedAuth } from '../../../context/UnifiedAuthContext';
import { USER_ROLES } from '../../../config/userRoles';
import { Card, Button } from '../../../components/ui/BaseComponents';
import { ConfirmDialog } from '../../../components/ui/ConfirmDialog';
import { AlertBanner } from '../../../components/ui/CommonComponents';
import ManageMembersModal from './components/ManageMembersModal';
import { Logger } from '../../../utils/logger';

const log = Logger.create('CoursesTab');

function CoursesTab() {
  const { userProfile } = useUnifiedAuth();
  const [courses, setCourses] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [courseToDelete, setCourseToDelete] = useState(null);

  // Members modal state
  const [membersModalOpen, setMembersModalOpen] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);

  // Fetch all courses
  useEffect(() => {
    fetchCourses();
  }, []);

  const fetchCourses = async () => {
    setLoading(true);
    setError(null);
    try {
      // Get all courses from Firestore
      const coursesArray = await getAllCourses();

      // Enrich with owner info
      const enrichedCourses = await Promise.all(
        coursesArray.map(async (course) => {
          let ownerName = 'Unbekannt';
          
          if (course.owner) {
            try {
              const ownerProfile = await getUserProfile(course.owner);
              ownerName = ownerProfile?.displayName || ownerProfile?.email || 'Unbekannt';
            } catch (err) {
              log.warn(`Could not load owner profile for ${course.owner}`, err);
            }
          }

          return {
            ...course,
            ownerName,
            memberCount: Object.keys(course.members || {}).length,
            quizCount: Object.keys(course.quizzes || {}).length
          };
        })
      );

      // Sort by creation date (newest first)
      enrichedCourses.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));

      setCourses(enrichedCourses);
    } catch (err) {
      log.error('Error fetching courses:', err);
      setError('Fehler beim Laden der Kurse');
    } finally {
      setLoading(false);
    }
  };

  // Check if user can manage course
  const canManageCourse = (course) => {
    return userProfile?.role === USER_ROLES.ADMIN || course.owner === userProfile?.uid;
  };

  // Filter courses by search query
  const filteredCourses = courses.filter(course => {
    const searchLower = searchQuery.toLowerCase();
    return (
      course.name.toLowerCase().includes(searchLower) ||
      course.ownerName.toLowerCase().includes(searchLower)
    );
  });

  // Copy invite link
  const copyInviteLink = async (inviteCode) => {
    try {
      const baseUrl = window.location.origin;
      const inviteUrl = `${baseUrl}/join/${inviteCode}`;
      await navigator.clipboard.writeText(inviteUrl);
      setSuccess('Link kopiert!');
      setTimeout(() => setSuccess(null), 2000);
    } catch (err) {
      log.error('Error copying link:', err);
      setError('Fehler beim Kopieren des Links');
    }
  };

  // Delete course from Firestore
  const handleDeleteCourse = async () => {
    if (!courseToDelete) return;

    try {
      // Delete course (cascade will handle related data via Firestore rules/functions)
      await deleteCourse(courseToDelete.id);

      setSuccess('Kurs gelöscht');
      setTimeout(() => setSuccess(null), 3000);
      
      // Refresh courses
      await fetchCourses();
    } catch (err) {
      log.error('Error deleting course:', err);
      setError('Fehler beim Löschen des Kurses');
    } finally {
      setCourseToDelete(null);
      setDeleteDialogOpen(false);
    }
  };

  const openDeleteDialog = (course) => {
    setCourseToDelete(course);
    setDeleteDialogOpen(true);
  };

  const openMembersModal = (course) => {
    setSelectedCourse(course);
    setMembersModalOpen(true);
  };

  // Format date
  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    const date = new Date(timestamp);
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit'
    });
  };

  return (
    <Card>
      {/* Alert Messages */}
      {error && (
        <AlertBanner variant="danger" className="mb-4">
          {error}
        </AlertBanner>
      )}
      {success && (
        <AlertBanner variant="success" className="mb-4">
          {success}
        </AlertBanner>
      )}

      {/* Search and Actions */}
      <div className="mb-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <input
          type="text"
          placeholder="Kurse durchsuchen..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="flex-1 px-4 py-2 border border-border rounded-lg bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-primary theme-transition"
        />
        <Button
          onClick={() => window.location.href = '/courses/create'}
        >
          + Neuer Kurs
        </Button>
      </div>

      {/* Loading State */}
      {loading && (
        <p className="text-center py-8 text-muted-foreground">Lade Kurse...</p>
      )}

      {/* Empty State */}
      {!loading && filteredCourses.length === 0 && !searchQuery && (
        <div className="text-center py-12">
          <p className="text-muted-foreground mb-4">Noch keine Kurse vorhanden</p>
          <Button onClick={() => window.location.href = '/courses/create'}>
            Ersten Kurs erstellen
          </Button>
        </div>
      )}

      {/* Search Empty State */}
      {!loading && filteredCourses.length === 0 && searchQuery && (
        <p className="text-center py-8 text-muted-foreground">
          Keine Kurse gefunden für "{searchQuery}"
        </p>
      )}

      {/* Courses Table */}
      {!loading && filteredCourses.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-border">
              <tr className="bg-muted/50">
                <th className="text-left py-3 px-4 font-medium text-foreground">Kursname</th>
                <th className="text-left py-3 px-4 font-medium text-foreground">Owner</th>
                <th className="text-center py-3 px-4 font-medium text-foreground">Mitglieder</th>
                <th className="text-center py-3 px-4 font-medium text-foreground">Quizzes</th>
                <th className="text-center py-3 px-4 font-medium text-foreground">Erstellt</th>
                <th className="text-center py-3 px-4 font-medium text-foreground">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {filteredCourses.map(course => (
                <tr 
                  key={course.id}
                  className="border-b border-border hover:bg-muted/50 theme-transition"
                >
                  <td className="py-3 px-4">
                    <div>
                      <p className="font-medium text-foreground">{course.name}</p>
                      {course.description && (
                        <p className="text-xs text-muted-foreground mt-1 line-clamp-1">
                          {course.description}
                        </p>
                      )}
                    </div>
                  </td>
                  <td className="py-3 px-4 text-muted-foreground">
                    {course.ownerName}
                  </td>
                  <td className="py-3 px-4 text-center text-foreground">
                    {course.memberCount}
                  </td>
                  <td className="py-3 px-4 text-center text-foreground">
                    {course.quizCount}
                  </td>
                  <td className="py-3 px-4 text-center text-muted-foreground text-xs">
                    {formatDate(course.createdAt)}
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex flex-wrap gap-2 justify-center">
                      {/* Copy Link Button */}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => copyInviteLink(course.inviteCode)}
                      >
                        Link kopieren
                      </Button>

                      {/* Manage Members Button */}
                      {canManageCourse(course) && (
                        <Button
                          variant="default"
                          size="sm"
                          onClick={() => openMembersModal(course)}
                        >
                          Mitglieder
                        </Button>
                      )}

                      {/* Delete Button (only for admin/owner) */}
                      {canManageCourse(course) && (
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => openDeleteDialog(course)}
                        >
                          Löschen
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setCourseToDelete(null);
        }}
        onConfirm={handleDeleteCourse}
        title="Kurs wirklich löschen?"
        message={`"${courseToDelete?.name}" wird mit allen Quizzes und Mitgliedschaften gelöscht.\n\nDiese Aktion kann nicht rückgängig gemacht werden.`}
        confirmLabel="Löschen"
        variant="danger"
      />

      {/* Manage Members Modal */}
      <ManageMembersModal
        isOpen={membersModalOpen}
        onClose={() => {
          setMembersModalOpen(false);
          setSelectedCourse(null);
        }}
        course={selectedCourse}
        onUpdate={fetchCourses}
      />
    </Card>
  );
}

export default CoursesTab;
